import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { CurrentUserDTO } from 'services/api/models/UserDTO';
import { updateUserProperties } from 'services/PostHogIdentity';
import { userActions } from 'store/users';

// A purchase does not reach our own records the moment it completes: RevenueCat
// posts to our webhook, and that handler calls their REST API back before it
// writes anything to the user. A refetch issued the instant `purchasePackage`
// resolves can easily beat all of it and come back still reading FREE, so
// `until` lets the caller say what it is waiting for and we look again.
const REFRESH_RETRY_DELAYS_MS = [0, 1000, 3000, 6000, 12000, 24000, 48000];

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

type RefreshOptions = {
  /**
   * Keep refetching until this holds for the user we get back. Without it the
   * first successful response wins. The user is dispatched on every attempt
   * either way, so a caller that is only nudging the store can omit it.
   */
  until?: (user: CurrentUserDTO) => boolean;
};

/**
 * Refetches `/api/auth/users/me/` and puts the result in the store.
 *
 * This is how the backend's `access_tier` — the source of truth for what a user
 * has access to — catches up after something changes it out of band. Resolves
 * with the last user fetched, or `undefined` if every attempt failed or `until`
 * never held.
 */
export function useRefreshCurrentUser() {
  const dispatch = useDispatch();

  return useCallback(
    async ({ until }: RefreshOptions = {}): Promise<CurrentUserDTO | undefined> => {
      let lastUser: CurrentUserDTO | undefined;

      for (const delay of REFRESH_RETRY_DELAYS_MS) {
        if (delay > 0) await wait(delay);

        try {
          const user = await ApiUsersManager.getUsersMe();
          dispatch(userActions.setCurrentUser(user));
          updateUserProperties(user);
          lastUser = user;
          if (!until || until(user)) return user;
        } catch (error) {
          Sentry.captureException(error, { tags: { refresh_current_user: 'failed' } });
        }
      }

      return lastUser;
    },
    [dispatch],
  );
}

export default useRefreshCurrentUser;
